import { Badge } from "reactstrap";
import { EnumFilter } from "../../../components/inputs";
import { LabOptionsFilter } from "../../components/filters/LabOptionsFilter";
import { PayorOptionsFilter } from "../../components/filters/PayorOptionsFilter";
import { ClinicOptionsFilter } from "../../components/filters/ClinicOptionsFilter";
import { Link } from "react-router";
import { Routes } from "../../Routes";
import { AdministrateEnumFilter } from "../../components/AdministrateEnumFilter";
import { useCallback } from "react";

import { Toolbar } from "../../../components/Toolbar";
import { AdministrateTable } from "../../components/AdministrateTable";
import { SystemConfigLayout } from "../../components/SystemConfigLayout";
import { DESTROY, UPDATE, useAdministrateTable, useCreatePermission } from "../../utils";
import { CountCell } from "../../../components/grid/cells/CountCell";
import { OnshoreConfigurationActionCell } from "./components/OnshoreConfigurationActionCell";
import { CreateOnshoreConfigurationModal } from "./components/CreateOnshoreConfigurationModal";
import { AdministrateBackgroundExportButton } from "../../components/AdministrateBackgroundExportButton";
import { ImportOnshoreConfigurationsModal } from "./components/ImportOnshoreConfigurationsModal";

export const classificationOptions = [
  { label: "Onshore", value: "onshore" },
  { label: "Offshore", value: "offshore" },
];

const ClassificationCell = ({ value }) => <Badge color={value === "onshore" ? "success" : "info"}>{value}</Badge>;

const columns = [
  {
    id: "id",
    accessor: "id",
    Header: "ID",
    maxWidth: 70,
  },
  {
    id: "lab.id",
    accessor: "lab",
    Header: "Lab",
    disableSortBy: true,
    Filter: (props) => <LabOptionsFilter valueKey="id" {...props} />,
    Cell: ({ value }) =>
      value ? (
        <Link to={Routes.lab.url(value.id)} target="_blank">
          <span className="token">{value.labName}</span>
        </Link>
      ) : (
        <span className="token">All Labs</span>
      ),
  },
  {
    id: "clinic",
    accessor: "clinic",
    Header: "Clinic",
    disableSortBy: true,
    Filter: (props) => <ClinicOptionsFilter valueKey="id" {...props} />,
    Cell: ({ value }) =>
      value ? (
        <Link to={Routes.clinic.url(value.id)} target="_blank">
          <span className="token">{value.clinicName}</span>
        </Link>
      ) : (
        <span className="token">All Clinics</span>
      ),
  },
  {
    id: "payor",
    accessor: "payor",
    Header: "Payor",
    disableSortBy: true,
    Filter: (props) => <PayorOptionsFilter {...props} />,
    Cell: ({ value }) =>
      value ? (
        <Link to={Routes.payor.url(value.id)} target="_blank">
          <span className="token">{value.name}</span>
        </Link>
      ) : (
        <span className="token">All Payors</span>
      ),
  },
  {
    id: "test",
    accessor: "test",
    Header: "Test(s)",
    disableSortBy: true,
    Cell: ({ value }) => <span className="token">{value ? value.commercialName : "All Tests"}</span>,
    Filter: (props) => <AdministrateEnumFilter {...props} isMulti resourceUrl="/admin/tests.json" valueKey="id" />,
  },
  {
    id: "codes",
    accessor: "cptCodes",
    Header: "Code(s)",
    disableSortBy: true,
    disableFilters: true,
    Cell: ({ value }) => (
      <div title={value}>
        <CountCell count={value?.length} label="Code" />
      </div>
    ),
  },
  {
    id: "classification",
    accessor: "classification",
    Header: "Classification",
    disableSortBy: true,
    Cell: ClassificationCell,
    Filter: (props) => <EnumFilter {...props} options={classificationOptions} />,
    maxWidth: 90,
  },
  {
    id: "actions",
    Header: "Actions",
    Cell: OnshoreConfigurationActionCell,
    disableFilters: true,
    disableSortBy: true,
    minWidth: 220,
  },
];

export const OnshoreConfigurationIndex = () => {
  const { tableRef, allFilters, setTableFilters } = useAdministrateTable();

  const [canCreate] = useCreatePermission("OnshoreConfiguration");

  const onAdd = useCallback(
    (message, errorDetails = []) => {
      tableRef.current?.refresh?.();
      tableRef.current?.setMessage?.(message, errorDetails.length ? "warning" : "success", errorDetails);
    },
    [tableRef]
  );

  const onImport = useCallback(
    (message, errorDetails = []) => {
      const { setMessage, refresh } = tableRef.current;

      setMessage(message, errorDetails.length ? "warning" : "success", errorDetails);
      refresh();
    },
    [tableRef]
  );

  return (
    <SystemConfigLayout title={["Automation", "Onshore Configuration"]}>
      <Toolbar className="align-items-center mb-3">
        <AdministrateBackgroundExportButton
          outline
          className="me-2"
          exportUrl="/admin/onshore_configurations/export"
          filters={allFilters}
          ref={tableRef}
        />
        {canCreate && (
          <>
            <ImportOnshoreConfigurationsModal onImport={onImport} className="me-2" />
            <CreateOnshoreConfigurationModal onAdd={onAdd} color="primary" />
          </>
        )}
      </Toolbar>

      <AdministrateTable
        resourceUrl={"/admin/onshore_configurations.json"}
        resourceName={"onshore_configurations"}
        permissions={[UPDATE, DESTROY]}
        onFilteredChange={setTableFilters}
        columns={columns}
        ref={tableRef}
      />
    </SystemConfigLayout>
  );
};
